import { useMemo } from 'react';

import { useGetCycleInstance } from '@/api/cycle-evaluation-services/cycle-instance';
import { useGetUserPermissions, useGetUsersByIds } from '@/api/directory';
import { getReviewees } from '@/utils/reviewAudience';
import { getRevieweesTransformedData, type Reviewee } from '@/utils/reviewEvaluatorSelection';

/** The 360 on the step, and its reviewees with the reviewers the server holds for them. */
export const useAssignReviewerData = (cycleInstanceId: string) => {
    const {
        data: cycleInstance,
        isLoading: isLoadingCycleInstance,
        isError: isCycleInstanceError,
    } = useGetCycleInstance(cycleInstanceId, {
        includeInactiveQuestions: true,
    });

    const { isLoading: permissionsLoading, isError: permissionsError } = useGetUserPermissions();

    // Extract reviewee IDs from cycle instance to fetch their details
    const revieweeIds = useMemo(() => {
        const reviewAudienceAssignments =
            cycleInstance?.reviewAudience?.reviewAudienceAssignments || [];

        return [...new Set(reviewAudienceAssignments.map((assignment) => assignment.assignedTo))];
    }, [cycleInstance?.reviewAudience?.reviewAudienceAssignments]);

    // Extract reviewer IDs from cycle instance to fetch reviewers that may not be in loaded pages
    const reviewerIds = useMemo(() => {
        const reviewEvaluatorAssignments =
            cycleInstance?.reviewEvaluatorSelection?.reviewEvaluatorAssignments || [];

        return [...new Set(reviewEvaluatorAssignments.map((assignment) => assignment.assignedTo))];
    }, [cycleInstance?.reviewEvaluatorSelection?.reviewEvaluatorAssignments]);

    const requiredUserIds = useMemo(
        () => [...new Set([...revieweeIds, ...reviewerIds])],
        [revieweeIds, reviewerIds],
    );

    // Fetch required user details (reviewees + assigned reviewers) that might not be in loaded pages
    const {
        data: requiredUsers,
        isError: requiredUsersError,
        isPending: requiredUsersLoading,
    } = useGetUsersByIds({
        userIds: requiredUserIds,
        identifier: `reviewer-assignment-${cycleInstanceId}`,
    });

    const baseReviewees: Reviewee[] = useMemo(() => {
        const users = requiredUsers || [];

        const reviewees = getReviewees({
            reviewAudience: cycleInstance?.reviewAudience,
            users,
        });

        return getRevieweesTransformedData({
            reviewAudience: cycleInstance?.reviewAudience,
            reviewEvaluatorSelection: cycleInstance?.reviewEvaluatorSelection,
            reviewees,
            users,
        });
    }, [cycleInstance?.reviewAudience, cycleInstance?.reviewEvaluatorSelection, requiredUsers]);

    return {
        cycleInstance,
        baseReviewees,
        isLoading: isLoadingCycleInstance || permissionsLoading || requiredUsersLoading,
        fetchErrors: [isCycleInstanceError, permissionsError, requiredUsersError],
    };
};
